import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { LocalStorage } from 'angular-web-storage';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import {
  EventDiscount,
  EventDiscountFilters, loadEventDiscountFiltersFromGetParams
} from '../../../../../../models/event-type';
import { EventTypeService } from '../../../../../../services/event-type/event-type.service';
import { ListResponse } from '../../../../../../models/list-response';
import { addFiltersToGetParams, dateStringFromISO8601 } from '../../../../../../utils';
import { AccountService } from '../../../../../../services/account/account.service';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { EventDiscountType, EventTypeStatus } from '../../../../../../models/event-type-enums';


@Component({
  selector: 'app-event-discounts',
  templateUrl: './event-discounts.component.html',
  styleUrls: ['./event-discounts.component.css']
})
export class EventDiscountsComponent implements OnInit {
  public pageNumber = 1;
  @LocalStorage() public pageSize;
  public filters: EventDiscountFilters = {};

  public filtersChangedSubject = new Subject<null>();

  public resultList: ListResponse<EventDiscount>;

  public loadingResults = false;

  public serverError: number = null;

  // Make browser history available to the template.
  public history: History;

  public dateStringFromISO8601 = dateStringFromISO8601;
  public EventTypeStatus = EventTypeStatus;
  public EventDiscountType = EventDiscountType;

  // The number of list requests. Used to ensure that only the most recent request
  // is displayed, in the event of concurrent requests (due to, e.g., filtering).
  private listRequestCount = 0;

  constructor(
    public accountService: AccountService,
    public orgConfig: OrganizationConfigService,
    private activatedRoute: ActivatedRoute,
    private eventTypeService: EventTypeService,
    private router: Router
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_events) {
      return;
    }

    if (!this.pageSize) {
      this.pageSize = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSize = this.pageSize;

    // Make browser history available to the template.
    this.history = history;

    // Handle changes to the filters by reloading the results, subject to debounce.
    this.filtersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadDiscounts();
      addFiltersToGetParams(
        this.filters,
        this.router,
        this.activatedRoute
      );
    });

    // Load initial query parameters from URL if present.
    this.filters = loadEventDiscountFiltersFromGetParams(this.activatedRoute);

    this.loadDiscounts();
  }

  /**
   * Load the page of results corresponding to the current page and filter selections.
   */
  public loadDiscounts() {
    this.loadingResults = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.eventTypeService
      .getEventDiscounts(this.pageNumber, this.pageSize, this.filters)
      .then(response => {
        // Only update the results if there has not been a more recent request initiated.
        if (thisRequestCount === this.listRequestCount) {
          this.resultList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingResults = false;
        }
      });
  }

  /**
   * Update the current page number to the given value and load the page of
   * results.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.loadDiscounts();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.resultList) { return 1; }
    return Math.ceil(this.resultList.count / this.pageSize);
  }

  /**
   * Handle a change in the filters.
   */
  public handleFilterChange() {
    this.pageNumber = 1;

    this.filtersChangedSubject.next();
  }

  /**
   * Whether a non-zero number of results have been loaded.
   */
  public resultsPresent(): boolean {
    return (
      this.resultList &&
      this.resultList.results &&
      (this.resultList.results.length > 0)
    );
  }
}
